/**
 * Workstation mappings (1.0.0-rc.9): how editors' computers see a location's folder or bucket. A location (an edit
 * location, or a storage location whose originals are linked in place) keeps a list of named mappings, each for one
 * operating system: a Windows share (\\nas01\edit) or drive (E:\), or a macOS or Linux mount point (/Volumes/Edit).
 * Offices that mount the same share differently each get a mapping; one mapping per operating system is its default.
 *
 * Shared by the API (validation, timelines, check-in) and the web app (the editor, the preview, the Send to edit
 * dialog). The path helpers here are the ones the timeline writers use (packages/nle re-exports them).
 */
import { z } from 'zod';
import { TARGET_OS, TARGET_OS_LABELS, type TargetOs } from './handoffs.ts';

/** At most this many mappings per location. */
export const MAX_WORKSTATION_MAPPINGS = 20;

export interface WorkstationMappingDTO {
  id: string;
  name: string;
  os: TargetOs;
  /** A Windows share or drive, or an absolute POSIX path; no trailing separator (except a drive's root, E:\). */
  path: string;
  /** The default for its operating system (at most one per OS). */
  isDefault: boolean;
}

/** A mapping as a hand-off keeps it (a snapshot: later edits of the location do not change a sent hand-off). */
export type WorkstationMappingSnapshot = Pick<WorkstationMappingDTO, 'id' | 'name' | 'os' | 'path'>;

type PathStyle = 'posix' | 'drive' | 'unc';

/** A path's style: a UNC share (\\server\share), a Windows drive (E:\, E:/), or POSIX. */
export function pathStyle(path: string): PathStyle {
  if (/^(\\\\|\/\/)[^\\/]+[\\/][^\\/]+/.test(path)) return 'unc';
  if (/^[A-Za-z]:([\\/]|$)/.test(path)) return 'drive';
  return 'posix';
}

/** The segments of a relative path (forward or back slashes), without empty or dot segments; refuses `..`. */
export function relativeSegments(rel: string): string[] {
  const parts = rel.replace(/\\/g, '/').split('/').filter((p) => p && p !== '.');
  if (parts.some((p) => p === '..' || p.includes('\0'))) throw new Error(`A hand-off path must stay inside its folder: ${JSON.stringify(rel)}`);
  return parts;
}

/** `base` joined with relative parts, in base's own style (backslashes on Windows drives and shares). */
export function joinWorkstationPath(base: string, ...rel: string[]): string {
  const style = pathStyle(base);
  const parts = rel.flatMap((r) => relativeSegments(r));
  if (style === 'posix') {
    const root = base.replace(/\/+$/, '');
    return [root || '', ...parts].join('/') || '/';
  }
  return [base.replace(/[\\/]+$/, ''), ...parts].join('\\');
}

/** A mapping's path as stored: trimmed, Windows paths with backslashes, no trailing separator (E:\ keeps its root). */
export function normalizeWorkstationPath(os: TargetOs, input: string): string {
  const p = input.trim();
  if (!p) return '';
  if (os === 'windows') {
    const back = p.replace(/\//g, '\\');
    if (/^[A-Za-z]:\\*$/.test(back)) return `${back.slice(0, 1).toUpperCase()}:\\`;
    if (/^[A-Za-z]:/.test(back)) return `${back.slice(0, 1).toUpperCase()}${back.slice(1).replace(/\\+$/, '')}`;
    return back.replace(/(.)\\+$/, '$1');
  }
  return p === '/' ? '/' : p.replace(/\/+$/, '') || '/';
}

/** Why a path cannot be a mapping for the operating system (null: it can). Checks the normalised form. */
export function workstationPathProblem(os: TargetOs, input: string): string | null {
  const p = normalizeWorkstationPath(os, input);
  if (!p) return 'Give the path';
  if (p.length > 500) return 'At most 500 characters';
  if (p.includes('\0')) return 'The path has a NUL character';
  const segments = p.replace(/\\/g, '/').split('/');
  if (segments.some((s) => s === '..')) return 'No .. in the path';
  if (os === 'windows') {
    if (/^\\\\[^\\]+\\[^\\]+(\\.*)?$/.test(p) || /^[A-Z]:\\(.*)?$/.test(p)) return null;
    return 'A Windows path is a share (\\\\server\\share) or a drive (E:\\)';
  }
  if (!p.startsWith('/') || p.startsWith('//')) return `A ${TARGET_OS_LABELS[os]} path is absolute and starts with / (e.g. ${os === 'mac' ? '/Volumes/Edit' : '/mnt/edit'})`;
  return null;
}

/** A problem with one field of a mapping list, for inline messages (index into the list). */
export interface MappingProblem { index: number; field: 'name' | 'path' | 'isDefault' | 'list'; message: string }

/** Everything wrong with a list of mappings: paths, names (unique, case-insensitive), one default per OS, the count. */
export function workstationMappingProblems(list: Array<Pick<WorkstationMappingDTO, 'name' | 'os' | 'path' | 'isDefault'>>): MappingProblem[] {
  const out: MappingProblem[] = [];
  if (list.length > MAX_WORKSTATION_MAPPINGS) out.push({ index: -1, field: 'list', message: `At most ${MAX_WORKSTATION_MAPPINGS} mappings per location` });
  const names = new Map<string, number>();
  const defaults = new Map<TargetOs, number>();
  list.forEach((m, index) => {
    const name = m.name.trim();
    if (!name) out.push({ index, field: 'name', message: 'Give the mapping a name' });
    else if (name.length > 60) out.push({ index, field: 'name', message: 'At most 60 characters' });
    else if (names.has(name.toLowerCase())) out.push({ index, field: 'name', message: `“${name}” is already a mapping here` });
    else names.set(name.toLowerCase(), index);
    const why = workstationPathProblem(m.os, m.path);
    if (why) out.push({ index, field: 'path', message: why });
    if (m.isDefault) {
      if (defaults.has(m.os)) out.push({ index, field: 'isDefault', message: `Only one ${TARGET_OS_LABELS[m.os]} mapping can be the default` });
      else defaults.set(m.os, index);
    }
  });
  return out;
}

/**
 * Default as a radio within each OS (rc.9 fixes): mapping `index` becomes (or stops being) the default, and when it
 * becomes one, any other default of its OS stops being one. The editor applies it as you tick.
 */
export function setDefaultMapping<T extends Pick<WorkstationMappingDTO, 'os' | 'isDefault'>>(list: T[], index: number, on = true): T[] {
  const os = list[index]?.os;
  if (!os) return list;
  return list.map((m, i) => (i === index ? { ...m, isDefault: on } : on && m.isDefault && m.os === os ? { ...m, isDefault: false } : m));
}

const shortId = () => Math.random().toString(36).slice(2, 10);

/** A list of mappings as the API accepts it: each one normalised, an id given to new ones, the problems refused. */
export const WorkstationMappingsSchema = z.array(z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,40}$/).optional(),
  name: z.string().max(200),
  os: z.enum(TARGET_OS),
  path: z.string().max(1000),
  isDefault: z.boolean().default(false),
})).max(MAX_WORKSTATION_MAPPINGS, `At most ${MAX_WORKSTATION_MAPPINGS} mappings per location`).superRefine((list, ctx) => {
  for (const p of workstationMappingProblems(list)) ctx.addIssue({ code: 'custom', message: p.message, path: p.index >= 0 ? [p.index, p.field] : [] });
}).transform((list): WorkstationMappingDTO[] => {
  const ids = new Set<string>();
  return list.map((m) => {
    let id = m.id && !ids.has(m.id) ? m.id : `m_${shortId()}`;
    while (ids.has(id)) id = `m_${shortId()}`;
    ids.add(id);
    return { id, name: m.name.trim(), os: m.os, path: normalizeWorkstationPath(m.os, m.path), isDefault: m.isDefault };
  });
});

/** A location's mappings from its config (anything malformed left out). */
export function workstationMappingsOf(config: unknown): WorkstationMappingDTO[] {
  const raw = ((config ?? {}) as { workstationMappings?: unknown }).workstationMappings;
  if (!Array.isArray(raw)) return [];
  return raw.filter((m): m is WorkstationMappingDTO => Boolean(m && typeof m === 'object' && typeof m.id === 'string' && typeof m.name === 'string' && TARGET_OS.includes(m.os) && typeof m.path === 'string'))
    .map((m) => ({ id: m.id, name: m.name, os: m.os, path: m.path, isDefault: Boolean(m.isDefault) }));
}

/** rc.6's `workstationPaths {mac, windows, linux}` as named mappings, each the default for its OS (migration 0064 does the same in SQL). */
export function workstationPathsToMappings(paths: Partial<Record<TargetOs, unknown>> | null | undefined): WorkstationMappingDTO[] {
  const out: WorkstationMappingDTO[] = [];
  for (const os of ['mac', 'windows', 'linux'] as const) {
    const p = paths?.[os];
    if (typeof p === 'string' && p.trim()) out.push({ id: os, name: TARGET_OS_LABELS[os], os, path: normalizeWorkstationPath(os, p), isDefault: true });
  }
  return out;
}

/** The operating system a browser runs on, from its user agent (macOS when unknown: most edit suites). */
export function browserOs(userAgent: string): TargetOs {
  if (/Windows/i.test(userAgent)) return 'windows';
  if (/Linux|X11|CrOS/i.test(userAgent) && !/Android/i.test(userAgent)) return 'linux';
  return 'mac';
}

/**
 * The mapping the Send to edit dialog starts with: the person's last choice for the location (if it is still there),
 * else the location's default for the browser's OS, else the first one; null for a location without mappings.
 */
export function chooseMapping(mappings: WorkstationMappingDTO[], opts: { lastId?: string | null; os: TargetOs }): WorkstationMappingDTO | null {
  return mappings.find((m) => m.id === opts.lastId) ?? mappings.find((m) => m.os === opts.os && m.isDefault) ?? mappings.find((m) => m.os === opts.os) ?? mappings[0] ?? null;
}

/**
 * In place: the mapping of a storage location that matches the hand-off's: the same OS and the same name
 * (case-insensitive), else that OS's default, else null (the original's path is then not known on that OS).
 */
export function matchingMapping(mappings: WorkstationMappingDTO[], chosen: Pick<WorkstationMappingDTO, 'os' | 'name'>): WorkstationMappingDTO | null {
  const same = mappings.filter((m) => m.os === chosen.os);
  return same.find((m) => m.name.trim().toLowerCase() === chosen.name.trim().toLowerCase()) ?? same.find((m) => m.isDefault) ?? null;
}

/**
 * The part of an NLE's file path under a mapping's path, as relative segments joined with `/`; null when the path
 * is not under it. Separators are compared loosely (\ and /), Windows paths and UNC hosts case-insensitively,
 * POSIX paths exactly. Accepts file:// URLs (as some NLEs write them).
 */
export function workstationRelative(path: string, base: string): string | null {
  const plain = fromFileUrl(path);
  const style = pathStyle(base);
  const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '');
  const b = norm(base);
  const p = norm(plain);
  const ci = style !== 'posix';
  const bb = ci ? b.toLowerCase() : b;
  const pp = ci ? p.toLowerCase() : p;
  if (bb === '' || bb === '/') return p.startsWith('/') ? p.slice(1).split('/').filter(Boolean).join('/') || null : null;
  if (pp === bb || !pp.startsWith(`${bb}/`)) return null;
  const rest = p.slice(b.length + 1).split('/').filter((s) => s && s !== '.');
  if (!rest.length || rest.includes('..')) return null;
  return rest.join('/');
}

/** A file:// URL as a path (FCP7's file://localhost/E%3a/…, FCPXML's file:///E:/…, file://nas01/edit/… for UNC). */
export function fromFileUrl(path: string): string {
  if (!/^file:\/\//i.test(path)) return path;
  let rest = path.slice(7);
  let host = '';
  const slash = rest.indexOf('/');
  host = slash < 0 ? rest : rest.slice(0, slash);
  rest = slash < 0 ? '' : rest.slice(slash);
  let decoded: string;
  try { decoded = decodeURIComponent(rest); } catch { decoded = rest; }
  if (host && host.toLowerCase() !== 'localhost') return `\\\\${host}${decoded.replace(/\//g, '\\')}`;
  if (/^\/[A-Za-z]:/.test(decoded)) return decoded.slice(1).replace(/\//g, '\\');
  return decoded;
}

/** A mapping for a list or a picker: "Mac (Helsinki) · macOS". */
export const mappingLabel = (m: Pick<WorkstationMappingDTO, 'name' | 'os'>) => `${m.name} · ${TARGET_OS_LABELS[m.os]}`;
